import React, {useEffect, useState} from 'react';
import {Box, Stack, Typography, Tab, Tabs, IconButton, Menu, MenuItem} from '@mui/material';
import {useAtom, useAtomValue} from 'jotai';
import {HugeiconsIcon} from '@hugeicons/react';
import {
  Delete01Icon, MoreVerticalIcon, PencilEdit01Icon, ArrowLeft01Icon, GlobeIcon, File02Icon, Calendar03Icon, Clock01Icon,
  UserIcon, FlashIcon,
} from '@hugeicons/core-free-icons';
import {toast} from 'react-toastify';
import {KnowledgeManagerRepository} from '../../../../repositories/KnowledgeManagerRepository';
import {ConfirmModalNew} from '../../mcp-discovery/components/ConfirmModalNew';
import {selectedTopicAtom, detailTabAtom, documentsAtom, projectAtom, queriesAtom} from '../atoms';
import {EditTopicModalNew} from './EditTopicModalNew';
import {DocumentsTab} from './DocumentsTab';
import {QueryTab} from './QueryTab';
import {GraphTab} from './GraphTab';
import {SettingsTab} from './SettingsTab';
import {subProjectLabel} from '../../../../utils/subProjects';

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
};

const MetaTile = ({icon, label, children}) => (
  <Box sx={{flex: "1 1 150px", bgcolor: "#FAFAFD", borderRadius: "12px", p: 1.5, minWidth: 0}}>
    <Stack direction="row" alignItems="center" gap={0.5}>
      <HugeiconsIcon icon={icon} size={12} color={"#8A8AA0"}/>
      <Typography sx={{fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", color: "#8A8AA0", textTransform: "uppercase"}}>{label}</Typography>
    </Stack>
    <Typography noWrap sx={{fontSize: 13, fontWeight: 600, color: "#23264D", mt: 0.5}}>{children}</Typography>
  </Box>
);

export const TopicDetailsPanel = ({onTopicChanged}) => {
  const [selectedTopic, setSelectedTopic] = useAtom(selectedTopicAtom);
  const [selectedTab, setSelectedTab] = useAtom(detailTabAtom);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const documents = useAtomValue(documentsAtom);
  const [queries, setQueries] = useAtom(queriesAtom);
  const project = useAtomValue(projectAtom);

  useEffect(() => {
    if (!selectedTopic?.id) {
      setQueries([]);
      return;
    }
    let cancelled = false;
    KnowledgeManagerRepository.listQueries(selectedTopic.id, {limit: 50})
      .then(list => {if (!cancelled) setQueries(list);})
      .catch(() => {if (!cancelled) setQueries([]);});
    return () => {cancelled = true;};
  }, [selectedTopic?.id, setQueries]);

  if (!selectedTopic) {
    return (
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, px: 3, textAlign: 'center'}}>
        <Box>
          <Typography sx={{fontSize: 15, fontWeight: 700, color: '#23264D'}}>No topic selected</Typography>
          <Typography sx={{fontSize: 13, color: '#8A8AA0', mt: 0.5}}>Pick a topic from the list to manage its documents and settings</Typography>
        </Box>
        <Stack
          direction="row" alignItems="center" gap={0.75} sx={{
            mt: 0.5, px: 1.5, py: 0.75, borderRadius: '999px', bgcolor: 'rgba(132, 145, 247, 0.08)', color: '#4B55C9',
            animation: 'km-point-left 1.4s ease-in-out infinite',
            '@keyframes km-point-left': {
              '0%, 100%': {transform: 'translateX(0)'},
              '50%': {transform: 'translateX(-5px)'},
            },
          }}
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} size={15}/>
          <Typography sx={{fontSize: 12, fontWeight: 700}}>Choose a topic from the list</Typography>
        </Stack>
      </Box>
    );
  }

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await KnowledgeManagerRepository.deleteTopic(selectedTopic.id);
      toast.success("Topic deleted");
      setSelectedTopic(null);
      setConfirmOpen(false);
      await onTopicChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete topic");
    } finally {
      setDeleting(false);
    }
  };

  const handleEditSaved = async (updated) => {
    setSelectedTopic(updated);
    setEditOpen(false);
    await onTopicChanged?.();
  };

  const subProject = (project?.subProjects || []).find(
    item => String(item.id) === String(selectedTopic.subProjectId),
  );
  const topicDocuments = documents.filter(d => d.topicId === selectedTopic.id);
  const documentsValue = topicDocuments.length > 0
    ? `${topicDocuments.filter(d => d.status === 'indexed').length} indexed`
    : `${selectedTopic.documentCount || 0} total`;

  const topicQueries = queries.filter(q => q.topicId === selectedTopic.id);
  const timedQueries = topicQueries.filter(q => !!q.processingTimeMs);
  const avgResponseTimeMs = timedQueries.length > 0
    ? timedQueries.reduce((sum, q) => sum + q.processingTimeMs, 0) / timedQueries.length
    : null;

  return (
    <Stack sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
      <Box sx={{p: 3, pb: 2.5}}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{mb: 2}}>
          <Stack direction="row" alignItems="flex-start" gap={1.25} sx={{flex: 1, minWidth: 0}}>
            <Box sx={{width: 12, height: 12, borderRadius: "50%", bgcolor: selectedTopic.color || "#8491F7", mt: 0.5, flexShrink: 0}}/>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontSize: 16, fontWeight: 700, color: '#23264D'}} noWrap>{selectedTopic.name}</Typography>
              {selectedTopic.description && (
                <Typography sx={{fontSize: '12px', color: '#8A8AA0', mt: 0.5}}>{selectedTopic.description}</Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1}>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{'&:hover': {backgroundColor: 'rgba(132, 145, 247, 0.08)'}}}>
              <HugeiconsIcon icon={MoreVerticalIcon} size={18}/>
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} PaperProps={{sx: {borderRadius: '12px'}}}>
              <MenuItem onClick={() => {setMenuAnchor(null); setEditOpen(true);}} sx={{gap: 1}}>
                <HugeiconsIcon icon={PencilEdit01Icon} size={16}/><Typography sx={{fontSize: '13px'}}>Edit Topic</Typography>
              </MenuItem>
              <MenuItem onClick={() => {setMenuAnchor(null); setConfirmOpen(true);}} sx={{color: '#DC2626', gap: 1}}>
                <HugeiconsIcon icon={Delete01Icon} size={16}/><Typography sx={{fontSize: '13px'}}>Delete Topic</Typography>
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>

        <Stack direction="row" gap={1.25} flexWrap="wrap">
          <MetaTile icon={GlobeIcon} label="Sub project">
            {subProjectLabel(subProject) || "—"}
          </MetaTile>
          <MetaTile icon={File02Icon} label="Documents">{documentsValue}</MetaTile>
          <MetaTile icon={Calendar03Icon} label="Created">{formatDate(selectedTopic.createdAt)}</MetaTile>
          <MetaTile icon={Clock01Icon} label="Last updated">{formatDate(selectedTopic.updatedAt)}</MetaTile>
          {avgResponseTimeMs != null && (
            <MetaTile icon={FlashIcon} label="Avg response">{(avgResponseTimeMs / 1000).toFixed(1)}s</MetaTile>
          )}
          {selectedTopic.createdBy && (
            <MetaTile icon={UserIcon} label="Created by">{selectedTopic.createdBy}</MetaTile>
          )}
        </Stack>
      </Box>

      <Tabs
        value={selectedTab}
        onChange={(e, v) => setSelectedTab(v)}
        sx={{
          px: 3,
          '& .MuiTab-root': {textTransform: 'none', fontWeight: 700, fontSize: '13px', gap: 0.75, '&.Mui-selected': {color: '#8491F7'}},
          '& .MuiTabs-indicator': {backgroundColor: '#8491F7'},
        }}
      >
        <Tab label={`Documents${selectedTopic.documentCount ? ` (${selectedTopic.documentCount})` : ''}`} value="documents"/>
        <Tab label={"Query"} value="query"/>
        <Tab label={"Knowledge Graph"} value="graph"/>
        <Tab label={"Settings"} value="settings"/>
      </Tabs>

      <Box sx={{flex: 1, minHeight: 0, overflow: 'auto', p: 3}}>
        {selectedTab === 'documents' && <DocumentsTab topic={selectedTopic} onDocumentCountChanged={onTopicChanged}/>}
        {selectedTab === 'query' && <QueryTab topic={selectedTopic}/>}
        {selectedTab === 'graph' && <GraphTab topic={selectedTopic}/>}
        {selectedTab === 'settings' && <SettingsTab topic={selectedTopic}/>}
      </Box>

      <ConfirmModalNew
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title={`Delete "${selectedTopic.name}"?`}
        description="This will permanently remove the topic and all its documents. This action cannot be undone."
        confirmText="Delete Topic"
        loading={deleting}
      />

      <EditTopicModalNew open={editOpen} onClose={() => setEditOpen(false)} onSaved={handleEditSaved} topic={selectedTopic}/>
    </Stack>
  );
};
